/**
 * Checkout completion bookkeeping shared by the two ways a Stripe embedded
 * checkout can finish (Oct 2026 hotfix):
 *
 * 1. In-app: Stripe calls the embedded `onComplete` handler (the backend now
 *    sets `redirect_on_completion: 'if_required'`, so card payments stay
 *    in-app).
 * 2. Redirect: redirect-based payment methods (and sessions created before
 *    the backend change) navigate the whole tab to the backend's return_url,
 *    e.g. `https://app.mentorcoaching.ai/?status=success&session_id=cs_...`.
 *
 * Both paths must emit `checkout_completed` exactly once per Checkout
 * Session. Kept free of app imports so it can be unit-tested in node.
 */

const PENDING_CHECKOUT_KEY = 'mcai_pending_checkout';
const COMPLETED_KEY_PREFIX = 'mcai_checkout_completed:';
const CHECKOUT_RETURN_PATHS = new Set(['/', '/checkout-complete']);

export type PendingCheckout = {
  session_id?: string;
  plan?: string;
  billing_period?: string;
};

export type CheckoutReturn = {
  sessionId: string | null;
};

const claimedInMemory = new Set<string>();
let checkoutReturnConsumed = false;

/** Root layout: a Stripe return landing was consumed on this page load. */
export function markCheckoutReturnConsumed(): void {
  checkoutReturnConsumed = true;
}

/** True once the root layout consumed a Stripe return on this page load. */
export function wasCheckoutReturnConsumed(): boolean {
  return checkoutReturnConsumed;
}

let checkoutReturnMentorProfile: boolean | null = null;

/**
 * Root layout: whether the user returning from Stripe has a mentor profile,
 * read before the forced auth refresh so the public layout can send a tier3
 * mentor to Mentees on its first redirect (see postCheckoutRoute.ts).
 */
export function setCheckoutReturnMentorProfile(isMentor: boolean): void {
  checkoutReturnMentorProfile = isMentor;
}

/** Null when no Stripe return is being routed on this page load. */
export function peekCheckoutReturnMentorProfile(): boolean | null {
  return checkoutReturnMentorProfile;
}

/** The post-checkout redirect happened; later redirects route by phase. */
export function clearCheckoutReturnMentorProfile(): void {
  checkoutReturnMentorProfile = null;
}

function sessionStore(): Storage | null {
  try {
    const store = (globalThis as { sessionStorage?: Storage }).sessionStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

/** Remember which plan a just-created Checkout Session is for, so a full-page
 *  Stripe redirect back into the app can still report plan/cycle. */
export function rememberPendingCheckout(pending: PendingCheckout): void {
  try {
    sessionStore()?.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(pending));
  } catch {}
}

export function readPendingCheckout(): PendingCheckout | null {
  try {
    const raw = sessionStore()?.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as PendingCheckout) : null;
  } catch {
    return null;
  }
}

/**
 * Returns true the first time a completion is claimed for `sessionId` in
 * this tab, false for every later claim. Without a session id there is
 * nothing to dedupe on; callers strip the return params from the URL so a
 * reload cannot re-claim.
 */
export function claimCheckoutCompletion(sessionId?: string | null): boolean {
  if (!sessionId) return true;
  if (claimedInMemory.has(sessionId)) return false;
  claimedInMemory.add(sessionId);
  const store = sessionStore();
  try {
    if (store?.getItem(`${COMPLETED_KEY_PREFIX}${sessionId}`)) return false;
    store?.setItem(`${COMPLETED_KEY_PREFIX}${sessionId}`, '1');
  } catch {}
  return true;
}

/**
 * Detects a Stripe embedded-checkout success redirect landing.
 *
 * Matches `/?status=success` (prod's CHECKOUT_SUCCESS_URL) and any
 * `session_id=cs_...` on `/` or `/checkout-complete` (Testing's URL). Popup
 * windows are ignored: `/checkout-complete?status=success` inside a popup is
 * the credit-pack purchase flow, which reports back to its opener instead.
 */
export function parseCheckoutReturn(
  href: string | undefined | null,
  options: { isPopup?: boolean } = {},
): CheckoutReturn | null {
  if (!href || options.isPopup) return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const path = url.pathname.replace(/\/+$/, '') || '/';
  if (!CHECKOUT_RETURN_PATHS.has(path)) return null;
  const rawSessionId = url.searchParams.get('session_id');
  const sessionId = rawSessionId && rawSessionId.startsWith('cs_') ? rawSessionId : null;
  const status = (url.searchParams.get('status') || '').toLowerCase();
  if (sessionId) return { sessionId };
  if (path === '/' && status === 'success') return { sessionId: null };
  return null;
}

/** The same URL without the checkout-return params (for history.replaceState). */
export function stripCheckoutReturnParams(href: string): string {
  try {
    const url = new URL(href);
    url.searchParams.delete('status');
    url.searchParams.delete('session_id');
    return url.toString();
  } catch {
    return href;
  }
}

/** Test-only: forget in-memory claims. */
export function __resetCheckoutCompletionForTests(): void {
  claimedInMemory.clear();
  checkoutReturnConsumed = false;
  checkoutReturnMentorProfile = null;
}
